import React, { useState } from 'react';
import { Plus, Edit, Trash2, Clock } from 'lucide-react';

const RoutineEditor = ({ routine, onRoutineUpdate, isCaregiverMode, categories }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    idealTime: '',
    category: 'feeding',
    notes: '',
    repeat: 'daily'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingEvent) {
      // Update existing event
      const updatedRoutine = routine.map(event => 
        event.id === editingEvent.id 
          ? { ...event, ...formData }
          : event
      );
      onRoutineUpdate(updatedRoutine);
      setEditingEvent(null);
    } else {
      // Add new event
      const newEvent = {
        id: Date.now().toString(),
        ...formData
      };
      onRoutineUpdate([...routine, newEvent]);
    }
    
    setShowForm(false);
    setFormData({
      title: '',
      idealTime: '',
      category: 'feeding',
      notes: '',
      repeat: 'daily'
    });
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      idealTime: event.idealTime,
      category: event.category,
      notes: event.notes,
      repeat: event.repeat
    });
    setShowForm(true);
  };

  const handleDelete = (eventId) => {
    const updatedRoutine = routine.filter(event => event.id !== eventId);
    onRoutineUpdate(updatedRoutine);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      idealTime: '',
      category: 'feeding',
      notes: '',
      repeat: 'daily'
    });
  };

  const sortedRoutine = [...routine].sort((a, b) => a.idealTime.localeCompare(b.idealTime));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>Daily Routine</h2>
        {!isCaregiverMode && (
          <button 
            className="btn" 
            onClick={() => setShowForm(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={20} />
            Add Event
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3>{editingEvent ? 'Edit Event' : 'Add New Event'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Event Title</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Morning Feeding, Nap Time"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Ideal Time</label>
                <input
                  type="time"
                  className="form-control time-input"
                  value={formData.idealTime}
                  onChange={(e) => setFormData({...formData, idealTime: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  {Object.entries(categories).map(([key, category]) => (
                    <option key={key} value={key}>
                      {category.icon} {key.charAt(0).toUpperCase() + key.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Repeat</label>
              <select
                className="form-control"
                value={formData.repeat}
                onChange={(e) => setFormData({...formData, repeat: e.target.value})}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                className="form-control notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about this event..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn">
                {editingEvent ? 'Update Event' : 'Add Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        {sortedRoutine.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3>No routine events yet</h3>
            <p>Add your first event to get started!</p>
          </div>
        ) : (
          sortedRoutine.map(event => {
            const category = categories[event.category];
            return (
              <div key={event.id} className="event-item">
                <div className="event-info">
                  <div className="event-title">
                    <span style={{ marginRight: '8px', fontSize: '20px' }}>
                      {category.icon}
                    </span>
                    {event.title}
                  </div>
                  <div className="event-time">
                    <Clock size={14} style={{ marginRight: '4px' }} />
                    {event.idealTime} • {event.repeat} • {event.notes}
                  </div>
                </div>
                {!isCaregiverMode && (
                  <div className="event-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleEdit(event)}
                      style={{ padding: '8px 12px' }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDelete(event.id)}
                      style={{ padding: '8px 12px' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RoutineEditor; 